import type { OnDestroy } from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, effect, inject } from '@angular/core';
import { Router } from '@angular/router';
import type { CountdownBarItem } from '../../store/countdown-bar.store';
import { countdownBarStore } from '../../store/countdown-bar.store';
import { CountdownBarCollapsedComponent } from '../countdown-bar-collapsed/countdown-bar-collapsed';
import { CountdownBarGameComponent } from '../countdown-bar-game/countdown-bar-game';
import { CountdownBarHeaderComponent } from '../countdown-bar-header/countdown-bar-header';
import { CountdownBarPaginationComponent } from '../countdown-bar-pagination/countdown-bar-pagination';
import { CountdownBarSwipeController } from './countdown-bar-swipe.controller';
import { GameStatus, GamesTypes } from '@app/domain/enums/game';

const FIRST_INDEX = 0;
const INDEX_OFFSET = 1;
const NOT_FOUND_INDEX = -1;
const HIDE_ANIMATION_MS = 700;
const LIVE_TRANSITION_MS = 400;
const LIVE_REMOVAL_MS = 660;
const LIVE_EMPTY_HIDE_MS = 560;
const NEXT_ITEM_ENTER_MS = 620;

@Component({
  selector: 'bingo-countdown-bar',
  imports: [
    CountdownBarCollapsedComponent,
    CountdownBarHeaderComponent,
    CountdownBarGameComponent,
    CountdownBarPaginationComponent,
  ],
  templateUrl: './countdown-bar.html',
  styleUrl: './countdown-bar.scss',
  providers: [countdownBarStore],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.is-animating]': 'hostIsAnimating',
  },
})
export class CountdownBarComponent implements OnDestroy {
  private readonly store = inject(countdownBarStore);
  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);
  private hadItems = false;
  private pendingItems: CountdownBarItem[] | null = null;
  private pendingCollapsed = false;
  private readonly swipeGesture = new CountdownBarSwipeController(direction => {
    this.changeSlide(direction);
  });
  private readonly itemStatesById = new Map<number, CountdownBarItem['state']>;
  private readonly liveTransitionIds = new Set<number>;
  private readonly liveTransitionTimers = new Map<number, ReturnType<typeof setTimeout>>;
  private hideAnimationTimer: ReturnType<typeof setTimeout> | undefined;
  private unhideAnimationTimer: ReturnType<typeof setTimeout> | undefined;
  private liveRemovalTimer: ReturnType<typeof setTimeout> | undefined;
  private liveEmptyHideTimer: ReturnType<typeof setTimeout> | undefined;
  private nextItemEnterTimer: ReturnType<typeof setTimeout> | undefined;

  protected items: CountdownBarItem[] = [];
  protected collapsed = this.store.collapsed();
  protected displayCollapsed = this.collapsed;
  protected activeIndex = 0;
  protected shouldRender = false;
  protected playIntroAnimation = false;
  protected playHideAnimation = false;
  protected playUnhideAnimation = false;
  protected playLiveRemovalAnimation = false;
  protected playLiveEmptyHideAnimation = false;
  protected playNextItemAnimation = false;

  constructor() {
    effect(() => {
      const items = this.store.items();
      const collapsed = this.store.collapsed();

      this.onStoreUpdate(items, collapsed);
    });
  }

  get hostIsAnimating(): boolean {
    return this.isEnterAnimating() || this.isExitAnimating();
  }

  ngOnDestroy(): void {
    clearTimeout(this.hideAnimationTimer);
    clearTimeout(this.unhideAnimationTimer);
    clearTimeout(this.liveRemovalTimer);
    clearTimeout(this.liveEmptyHideTimer);
    clearTimeout(this.nextItemEnterTimer);
    this.swipeGesture.destroy();

    this.liveTransitionTimers.forEach(timer => {
      clearTimeout(timer);
    });
    this.liveTransitionTimers.clear();
  }

  protected open(): void {
    this.store.expand();
  }

  protected hide(event: Event): void {
    event.stopPropagation();
    this.store.collapse();
  }

  protected openGame(item: CountdownBarItem): void {
    if (this.swipeGesture.swipeConsumed) {
      this.swipeGesture.swipeConsumed = false;

      return;
    }

    if (item.type === GamesTypes.GOAL_RUSH) {
      const isLive = item.game.cards_count >= item.game.max_cards && item.game.status === GameStatus.RUNNING;

      void this.router.navigate(['/goal-rush', item.id, isLive ? 'live' : 'constructor']);

      return;
    }

    void this.router.navigate(['/live-game', item.id]);
  }

  protected goTo(index: number): void {
    this.activeIndex = index;
    this.cdr.markForCheck();
  }

  protected onPointerDown(event: PointerEvent): void {
    this.swipeGesture.onPointerDown(event);
  }

  protected onPointerUp(event: PointerEvent): void {
    this.swipeGesture.onPointerUp(event, this.items.length);
  }

  protected onWheel(event: WheelEvent): void {
    this.swipeGesture.onWheel(event, this.items.length);
  }

  protected barAnimationEnd(event: AnimationEvent): void {
    if (event.animationName === 'countdown-bar-enter') {
      this.playIntroAnimation = false;
    }

    if (event.animationName === 'countdown-bar-hide') {
      this.finishHideAnimation();
    }

    if (event.animationName === 'countdown-bar-unhide') {
      this.finishUnhideAnimation();
    }

    if (event.animationName === 'countdown-next-game-enter') {
      this.playNextItemAnimation = false;
    }
  }

  protected getActiveItem(): CountdownBarItem | null {
    return this.items.at(this.activeIndex) ?? this.items.at(FIRST_INDEX) ?? null;
  }

  protected isLiveTransitioning(item: CountdownBarItem): boolean {
    return this.liveTransitionIds.has(item.id);
  }

  private isEnterAnimating(): boolean {
    return this.playIntroAnimation || this.playUnhideAnimation || this.playNextItemAnimation;
  }

  private isExitAnimating(): boolean {
    return this.playLiveRemovalAnimation || this.playLiveEmptyHideAnimation || this.playHideAnimation;
  }

  private isCollapsedState(collapsed: boolean): boolean {
    return collapsed || this.collapsed || this.displayCollapsed;
  }

  private onStoreUpdate(items: CountdownBarItem[], collapsed: boolean): void {
    if (this.shouldAnimateLiveRemoval(items, collapsed)) {
      this.startLiveRemovalAnimation(items, collapsed);
    } else if (this.playLiveRemovalAnimation || this.playLiveEmptyHideAnimation) {
      this.pendingItems = items;
      this.pendingCollapsed = collapsed;
    } else {
      this.applyItems(items, collapsed, this.getActiveItem()?.id);
    }

    this.refreshView();
  }

  private shouldAnimateLiveRemoval(nextItems: CountdownBarItem[], collapsed: boolean): boolean {
    if (this.isExitAnimating() || this.isCollapsedState(collapsed)) {
      return false;
    }

    const activeItem = this.getActiveItem();

    if (activeItem?.state !== 'live') {
      return false;
    }

    return !nextItems.some(item => item.id === activeItem.id);
  }

  private applyItems(items: CountdownBarItem[], collapsed: boolean, preserveActiveId?: number): void {
    const wasCollapsed = this.collapsed;

    this.updateLiveTransitions(items);
    this.items = items;
    this.collapsed = collapsed;
    this.updateCollapseAnimation(wasCollapsed, collapsed, items.length > FIRST_INDEX);
    this.restoreActiveIndex(preserveActiveId);
  }

  private restoreActiveIndex(preserveActiveId: number | undefined): void {
    const preservedIndex = preserveActiveId === undefined
      ? NOT_FOUND_INDEX
      : this.items.findIndex(item => item.id === preserveActiveId);

    if (preservedIndex !== NOT_FOUND_INDEX) {
      this.activeIndex = preservedIndex;

      return;
    }

    this.clampActiveIndex();
  }

  private updateCollapseAnimation(wasCollapsed: boolean, collapsed: boolean, hasItems: boolean): void {
    if (this.shouldHide(wasCollapsed, collapsed)) {
      this.startHideAnimation();

      return;
    }

    if (this.shouldUnhide(wasCollapsed, collapsed, hasItems)) {
      this.startUnhideAnimation();

      return;
    }

    if (!this.playHideAnimation) {
      this.displayCollapsed = collapsed;
    }
  }

  private shouldHide(wasCollapsed: boolean, collapsed: boolean): boolean {
    return !wasCollapsed && collapsed;
  }

  private shouldUnhide(wasCollapsed: boolean, collapsed: boolean, hasItems: boolean): boolean {
    return wasCollapsed && !collapsed && hasItems && this.hadItems;
  }

  private startHideAnimation(): void {
    this.playIntroAnimation = false;
    this.playUnhideAnimation = false;
    this.playLiveRemovalAnimation = false;
    this.playLiveEmptyHideAnimation = false;
    this.playNextItemAnimation = false;
    this.playHideAnimation = true;
    this.displayCollapsed = false;
    clearTimeout(this.hideAnimationTimer);

    this.hideAnimationTimer = setTimeout(() => {
      this.finishHideAnimation();
    }, HIDE_ANIMATION_MS);
  }

  private startUnhideAnimation(): void {
    clearTimeout(this.hideAnimationTimer);
    clearTimeout(this.unhideAnimationTimer);
    this.playHideAnimation = false;
    this.displayCollapsed = false;
    this.playUnhideAnimation = true;

    this.unhideAnimationTimer = setTimeout(() => {
      this.finishUnhideAnimation();
    }, HIDE_ANIMATION_MS);
  }

  private finishUnhideAnimation(): void {
    if (!this.playUnhideAnimation) {
      return;
    }

    clearTimeout(this.unhideAnimationTimer);
    this.playUnhideAnimation = false;
    this.cdr.markForCheck();
  }

  private finishHideAnimation(): void {
    if (!this.playHideAnimation) {
      return;
    }

    clearTimeout(this.hideAnimationTimer);
    this.playHideAnimation = false;
    this.displayCollapsed = true;
    this.cdr.markForCheck();
  }

  private startLiveRemovalAnimation(nextItems: CountdownBarItem[], collapsed: boolean): void {
    this.pendingItems = nextItems;
    this.pendingCollapsed = collapsed;
    this.playIntroAnimation = false;
    this.playNextItemAnimation = false;
    this.playLiveEmptyHideAnimation = false;
    this.playLiveRemovalAnimation = true;
    clearTimeout(this.liveRemovalTimer);

    this.liveRemovalTimer = setTimeout(() => {
      this.finishLiveRemovalAnimation();
    }, LIVE_REMOVAL_MS);
  }

  private finishLiveRemovalAnimation(): void {
    if (!this.playLiveRemovalAnimation) {
      return;
    }

    const nextItems = this.pendingItems ?? [];

    if (nextItems.length === FIRST_INDEX) {
      this.startLiveEmptyHide();

      return;
    }

    const nextCollapsed = this.pendingCollapsed;

    this.playLiveRemovalAnimation = false;
    this.pendingItems = null;
    this.activeIndex = FIRST_INDEX;
    this.applyItems(nextItems, nextCollapsed);
    this.maybePlayNextItem(nextItems, nextCollapsed);
    this.refreshView();
  }

  private maybePlayNextItem(nextItems: CountdownBarItem[], nextCollapsed: boolean): void {
    if (nextItems.length > FIRST_INDEX && !nextCollapsed) {
      this.playNextItemAnimation = true;
      clearTimeout(this.nextItemEnterTimer);

      this.nextItemEnterTimer = setTimeout(() => {
        this.playNextItemAnimation = false;
        this.cdr.markForCheck();
      }, NEXT_ITEM_ENTER_MS);
    }
  }

  private startLiveEmptyHide(): void {
    this.playLiveEmptyHideAnimation = true;
    clearTimeout(this.liveEmptyHideTimer);

    this.liveEmptyHideTimer = setTimeout(() => {
      this.finishLiveEmptyHideAnimation();
    }, LIVE_EMPTY_HIDE_MS);
  }

  private finishLiveEmptyHideAnimation(): void {
    const nextItems = this.pendingItems ?? [];
    const nextCollapsed = this.pendingCollapsed;

    this.playLiveRemovalAnimation = false;
    this.playLiveEmptyHideAnimation = false;
    this.pendingItems = null;
    this.activeIndex = FIRST_INDEX;
    this.applyItems(nextItems, nextCollapsed);
    this.refreshView();
  }

  private updateLiveTransitions(items: CountdownBarItem[]): void {
    this.trackLiveTransitions(items);
    this.pruneLiveTransitions(items);
  }

  private trackLiveTransitions(items: CountdownBarItem[]): void {
    for (const item of items) {
      const previousState = this.itemStatesById.get(item.id);

      if (previousState === 'countdown' && item.state === 'live') {
        this.startLiveTransition(item.id);
      }

      this.itemStatesById.set(item.id, item.state);
    }
  }

  private pruneLiveTransitions(items: CountdownBarItem[]): void {
    const currentIds = new Set(items.map(item => item.id));

    for (const id of Array.from(this.itemStatesById.keys())) {
      if (!currentIds.has(id)) {
        this.itemStatesById.delete(id);
        this.stopLiveTransition(id);
      }
    }
  }

  private startLiveTransition(id: number): void {
    if (this.liveTransitionIds.has(id)) {
      return;
    }

    this.liveTransitionIds.add(id);
    clearTimeout(this.liveTransitionTimers.get(id));

    this.liveTransitionTimers.set(id, setTimeout(() => {
      this.stopLiveTransition(id);
    }, LIVE_TRANSITION_MS));
  }

  private stopLiveTransition(id: number): void {
    this.liveTransitionIds.delete(id);
    clearTimeout(this.liveTransitionTimers.get(id));
    this.liveTransitionTimers.delete(id);
    this.cdr.markForCheck();
  }

  private clampActiveIndex(): void {
    if (this.activeIndex >= this.items.length) {
      this.activeIndex = Math.max(FIRST_INDEX, this.items.length - INDEX_OFFSET);
    }
  }

  private changeSlide(direction: number): void {
    const next = this.activeIndex + direction;

    if (next < FIRST_INDEX || next >= this.items.length) {
      return;
    }

    this.activeIndex = next;
    this.cdr.markForCheck();
  }

  private updateRenderState(): void {
    const hasItems = this.items.length > FIRST_INDEX;
    const shouldRender = hasItems && this.store.isRouteAllowed();

    if (this.shouldPlayIntro(shouldRender, hasItems)) {
      this.playIntroAnimation = true;
    } else if (!hasItems) {
      this.playIntroAnimation = false;
      this.playHideAnimation = false;
      this.playUnhideAnimation = false;
    }

    this.hadItems = hasItems;
    this.shouldRender = shouldRender;
  }

  private shouldPlayIntro(shouldRender: boolean, hasItems: boolean): boolean {
    return shouldRender && !this.hadItems && !this.collapsed && hasItems;
  }

  private refreshView(): void {
    this.updateRenderState();
    this.cdr.markForCheck();
  }
}
